import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader, Users, FileText, Microscope, Calendar, Mail, Phone, CheckCircle } from 'lucide-react';
import { getResearchGroupById } from '../services/firestore';
import { capitalizeName } from '../utils/formatters';
import './ResearchGroupsInfo.css';

const ResearchGroupDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadGroupDetails();
  }, [id]);

  const loadGroupDetails = async () => {
    setLoading(true);
    const result = await getResearchGroupById(id);
    if (result.success) {
      setGroup(result.data);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="research-group-detail loading-state">
        <Loader size={48} className="spinner" color="#1A2B5B" />
        <p>Cargando información del grupo...</p>
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="research-group-detail error-state">
        <h2>Grupo no encontrado</h2>
        <p>{error || 'Este grupo no existe o ha sido eliminado.'}</p>
        <button className="back-button" onClick={() => navigate('/grupos-investigacion')}>
          <ArrowLeft size={20} /> Volver a Grupos
        </button>
      </div>
    );
  }

  return (
    <div className="research-group-detail">
      <button className="back-button" onClick={() => navigate('/grupos-investigacion')}>
        <ArrowLeft size={20} /> Volver
      </button>
      <div className="detail-header">
        {group.image ? (
          <div className="group-banner">
            <img src={group.image} alt={`Banner de ${group.name}`} />
          </div>
        ) : (
          <div className="group-image-placeholder">
            <Microscope size={64} className="placeholder-icon" />
            <div className="placeholder-abbr">{group.abbr}</div>
          </div>
        )}
        
        <div className="header-identity">
          <div className="group-logo-container">
            {group.logo ? (
              <img src={group.logo} alt={`Logo de ${group.name}`} className="group-logo" />
            ) : (
              <div className="group-logo-placeholder">
                <span>{group.abbr?.substring(0, 2)}</span>
              </div>
            )}
          </div>
          <div className="header-titles">
            <h1>{group.name}</h1>
            <div className="detail-tags">
              {group.tags && group.tags.map((tag, index) => (
                <span key={index} className="tag">{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      <div className="detail-content">
        <section className="detail-section">
          <h2><FileText size={20} /> Descripción Detallada</h2>
          <p className="description-text">
            {group.detailedDescription || group.description || 'Página en construcción. Pronto se subirán más detalles.'}
          </p>
        </section>
        
        <section className="detail-section">
          <h2>
            <Users size={20} /> 
            Equipo de Investigación 
            <span className="member-count-badge">({group.membersData?.length || 1} Miembros)</span>
          </h2>
          {group.membersData && group.membersData.length > 0 ? (
            <div className="members-grid">
              {group.membersData.map((member, index) => (
                <div key={index} className="member-card">
                  <div className="member-avatar-mini">
                    {member.photoURL ? (
                      <img src={member.photoURL} alt={member.fullName} className="member-photo-mini" />
                    ) : (
                      <Users size={16} />
                    )}
                  </div>
                  <span className="member-name">{capitalizeName(member.fullName)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p>Aún no hay miembros públicos registrados en este grupo.</p>
          )}
        </section>
        
        <section className="detail-section">
          <h2><Microscope size={20} /> Proyectos en Curso</h2>
          {group.projects && group.projects.length > 0 ? (
            <ul className="custom-list">
              {group.projects.map((project, index) => (
                <li key={index}>{project}</li>
              ))}
            </ul>
          ) : (
            <p className="empty-text">No hay proyectos listados actualmente.</p>
          )}
        </section>
        
        <section className="detail-section">
          <h2><Calendar size={20} /> Publicaciones Recientes</h2>
          {group.publications && group.publications.length > 0 ? (
            <ul className="custom-list">
              {group.publications.map((publication, index) => (
                <li key={index}>{publication}</li>
              ))}
            </ul>
          ) : (
            <p className="empty-text">No hay publicaciones registradas aún.</p>
          )}
        </section>
        <section className="detail-section">
          <h2><CheckCircle size={20} /> Requisitos para Unirse</h2>
          {group.requirements && group.requirements.length > 0 ? (
            <ul className="custom-list requirements-list">
              {group.requirements.map((req, index) => (
                <li key={index}>{req}</li>
              ))}
            </ul>
          ) : (
            <p className="empty-text">Contacta al equipo para conocer los requisitos específicos.</p>
          )}
        </section>
        <section className="detail-section join-cta-section">
          <h2>¿Cómo formar parte?</h2>
          <p>Para formar parte de este grupo oficial y obtener tu insignia de Investigador, debes contactar al equipo.</p>
          
          {group.contactEmail && (
            <div className="admin-contact">
              <Mail size={18} />
              <p><strong>Correo Oficial:</strong> <a href={`mailto:${group.contactEmail}`}>{group.contactEmail}</a></p>
            </div>
          )}
          
          {group.phone && (
            <div className="admin-contact">
              <Phone size={18} />
              <p><strong>Teléfono de Contacto:</strong> <span>{group.phone}</span></p>
            </div>
          )}
          
          {!group.contactEmail && !group.phone && (
            <div className="admin-contact">
              <p>El administrador de este grupo debe proporcionarte la <strong>Palabra Clave secreta</strong>.</p>
              <p>Una vez la tengas, ve a tu Panel de Tutor y dale a "Unirse al Grupo".</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ResearchGroupDetail;